import { type Draft, type PayloadAction } from "@reduxjs/toolkit"
import { type PanelRootState, type PanelStateBase } from "./create-panel-slice"

/**
 * Per-template chart view toggles, folded into the owning panel slice (asset-view / graph /
 * factors-view) so they persist per-panel and round-trip through a saved layout's viewConfig.
 *
 * Only ever `selectedCategory` and/or `selectedMetricDisplay`; both optional so each template stores
 * just the fields it owns. Defaults differ per template and live at the consumer's call site.
 */
export interface TemplateChartSettings {
  selectedCategory?: string | null
  selectedMetricDisplay?: string | null
}

/** Slice state carrying a per-template-key settings map. */
export interface WithTemplateCharts extends PanelStateBase {
  templateCharts: Partial<Record<string, TemplateChartSettings>>
}

export type SetTemplateChartPayload = PayloadAction<{
  dashboardId: string
  panelId: string
  templateKey: string
  settings: TemplateChartSettings
}>

/**
 * The two templateCharts reducers, shared by every panel slice that folds chart settings. `ensure`
 * comes from the slice factory; the draft is the plain object at runtime (the factory's documented
 * `as unknown as` shim), so we operate on `PanelRootState<S>` to sidestep the generic-`Draft<S>`
 * non-simplification (see create-panel-slice / project memory).
 */
export function makeTemplateChartReducers<S extends WithTemplateCharts>(
  ensure: (draft: Draft<PanelRootState<S>>, dashboardId: string, panelId: string) => S,
) {
  return {
    // Merge a partial settings change for one template key (header/chart toggles).
    setTemplateChartSetting(state: Draft<PanelRootState<S>>, action: SetTemplateChartPayload) {
      const { dashboardId, panelId, templateKey, settings } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      instance.templateCharts = {
        ...instance.templateCharts,
        [templateKey]: { ...instance.templateCharts[templateKey], ...settings },
      }
    },

    // Seed a template key's initial settings (e.g. from a drilldown). No-op once the key exists, so
    // a saved/loaded preference or a prior seed is never overwritten (mirrors the rule-33 guard).
    seedTemplateChart(state: Draft<PanelRootState<S>>, action: SetTemplateChartPayload) {
      const { dashboardId, panelId, templateKey, settings } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      if (instance.templateCharts[templateKey] != null) return
      instance.templateCharts = { ...instance.templateCharts, [templateKey]: settings }
    },
  }
}

/**
 * Session-only "duplicate from view" flow (APP-2025), folded into the owning panel slice
 * (asset-view / graph) the same way as templateCharts above. Never persisted (each field is
 * absent from the slice's PREFERENCE_FIELDS whitelist) and reset on clone by the consumer's
 * own cloneOverrides. `duplicateFromViewMetrics` is left as `unknown` here since asset-view and
 * graph disagree on its concrete shape (opaque `any` vs. `string[] | null`) — each slice keeps
 * its own narrower field type; only the shared reducer shape lives here.
 */
export interface WithDuplicateFlow extends PanelStateBase {
  showMetricsSelector: boolean
  duplicateFromViewName: string | null
  duplicateFromTemplateId: string | null
  duplicateFromViewMetrics: unknown
  duplicateReturnViewId: string | null
}

/**
 * The six duplicate-flow reducers, shared by every panel slice that offers a "duplicate this
 * view" entry point. Same `ensure`-parameterization as makeTemplateChartReducers above.
 */
export function makeDuplicateFlowReducers<S extends WithDuplicateFlow>(
  ensure: (draft: Draft<PanelRootState<S>>, dashboardId: string, panelId: string) => S,
) {
  return {
    setShowMetricsSelector(
      state: Draft<PanelRootState<S>>,
      action: PayloadAction<{ dashboardId: string, panelId: string, showMetricsSelector: boolean }>,
    ) {
      const { dashboardId, panelId, showMetricsSelector } = action.payload
      ensure(state, dashboardId, panelId).showMetricsSelector = showMetricsSelector
    },

    setDuplicateFromViewName(
      state: Draft<PanelRootState<S>>,
      action: PayloadAction<{ dashboardId: string, panelId: string, duplicateFromViewName: string | null }>,
    ) {
      const { dashboardId, panelId, duplicateFromViewName } = action.payload
      ensure(state, dashboardId, panelId).duplicateFromViewName = duplicateFromViewName
    },

    setDuplicateFromTemplateId(
      state: Draft<PanelRootState<S>>,
      action: PayloadAction<{ dashboardId: string, panelId: string, duplicateFromTemplateId: string | null }>,
    ) {
      const { dashboardId, panelId, duplicateFromTemplateId } = action.payload
      ensure(state, dashboardId, panelId).duplicateFromTemplateId = duplicateFromTemplateId
    },

    setDuplicateFromViewMetrics(
      state: Draft<PanelRootState<S>>,
      action: PayloadAction<{ dashboardId: string, panelId: string, duplicateFromViewMetrics: unknown }>,
    ) {
      const { dashboardId, panelId, duplicateFromViewMetrics } = action.payload
      ensure(state, dashboardId, panelId).duplicateFromViewMetrics = duplicateFromViewMetrics
    },

    setDuplicateReturnViewId(
      state: Draft<PanelRootState<S>>,
      action: PayloadAction<{ dashboardId: string, panelId: string, duplicateReturnViewId: string | null }>,
    ) {
      const { dashboardId, panelId, duplicateReturnViewId } = action.payload
      ensure(state, dashboardId, panelId).duplicateReturnViewId = duplicateReturnViewId
    },

    clearDuplicateState(
      state: Draft<PanelRootState<S>>,
      action: PayloadAction<{ dashboardId: string, panelId: string }>,
    ) {
      const { dashboardId, panelId } = action.payload
      const instance = ensure(state, dashboardId, panelId)
      instance.duplicateFromViewName = null
      instance.duplicateFromTemplateId = null
      instance.duplicateFromViewMetrics = null
      instance.duplicateReturnViewId = null
    },
  }
}
